package top.wcpe.allincore.ui.web.preview

/** application scope 分配的 bundle 身份；generation 与 sessionId 均不可复用。 */
internal data class PreviewBundleIdentity(
    val generation: Long,
    val sessionId: Long,
)

/** bundle 持有的单项可释放资源。 */
internal fun interface PreviewBundleResource {
    fun dispose()
}

/** candidate 与 current bundle 的完整所有权集合；before 在原子区，after 在新 current 可见后执行。 */
internal class PreviewBundleOwnership(
    val freezeDeliveries: () -> Unit,
    val resumeDeliveries: () -> Unit,
    val activate: () -> Unit,
    val beforeCurrentSwitch: () -> Unit,
    val afterCurrentSwitch: () -> Unit,
    val input: PreviewBundleResource,
    val modelHosts: List<PreviewBundleResource>,
    val surface: PreviewBundleResource,
    val mockSession: PreviewBundleResource,
    val resolverLeases: List<PreviewBundleResource>,
)

/** 未提交前隔离、提交后成为 current 的完整 bundle。 */
internal class PreviewBundleCandidate internal constructor(
    private val owner: Any,
    val identity: PreviewBundleIdentity,
    private val ownership: PreviewBundleOwnership,
) {
    private var activated = false
    private var frozen = false
    private var disposed = false

    internal val acceptsDeliveries: Boolean
        get() = activated && !frozen && !disposed

    internal fun belongsTo(expectedOwner: Any): Boolean = owner === expectedOwner

    internal fun ensureCommittable() {
        check(!disposed) { "候选预览 bundle 已关闭" }
        check(!activated) { "候选预览 bundle 已激活" }
    }

    internal fun freeze() {
        if (frozen || disposed) return
        frozen = true
        ownership.freezeDeliveries()
    }

    internal fun resume() {
        if (!frozen || disposed) return
        frozen = false
        ownership.resumeDeliveries()
    }

    internal fun activate() {
        check(!disposed) { "候选预览 bundle 已关闭" }
        check(!activated) { "候选预览 bundle 已激活" }
        activated = true
        ownership.activate()
    }

    internal fun prepareCurrentSwitch() {
        check(activated && !disposed) { "候选预览 bundle 尚未激活" }
        ownership.beforeCurrentSwitch()
    }

    internal fun completeCurrentSwitch() {
        check(activated && !disposed) { "候选预览 bundle 尚未激活" }
        ownership.afterCurrentSwitch()
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        releaseOwnedResources()
    }

    private fun releaseOwnedResources() {
        var failure: Throwable? = null
        failure = release(ownership.input, failure)
        failure = releaseReversed(ownership.modelHosts, failure)
        failure = release(ownership.surface, failure)
        failure = release(ownership.mockSession, failure)
        failure = releaseReversed(ownership.resolverLeases, failure)
        failure?.let { throw it }
    }

    private fun releaseReversed(
        resources: List<PreviewBundleResource>,
        initialFailure: Throwable?,
    ): Throwable? {
        var failure = initialFailure
        resources.asReversed().forEach { failure = release(it, failure) }
        return failure
    }

    private fun release(resource: PreviewBundleResource, initialFailure: Throwable?): Throwable? =
        try {
            resource.dispose()
            initialFailure
        } catch (error: Throwable) {
            initialFailure?.also { it.addSuppressed(error) } ?: error
        }
}

/** §2.2.2 bundle 两阶段提交的纯协调器，不负责创建具体浏览器资源。 */
internal class PreviewBundleLifecycle {
    private val owner = Any()
    private var generationSequence = 0L
    private var sessionSequence = 0L
    private var current: PreviewBundleCandidate? = null
    private var pending: PreviewBundleCandidate? = null
    private var commitInProgress = false
    private var disposed = false

    internal var commitCriticalSectionActive = false
        private set

    val currentIdentity: PreviewBundleIdentity?
        get() = current?.identity

    fun allocateCandidate(
        createOwnership: (PreviewBundleIdentity) -> PreviewBundleOwnership,
    ): PreviewBundleCandidate {
        ensureActive()
        check(!commitInProgress) { "预览 bundle 提交期间不能分配候选" }
        val identity = nextIdentity()
        val superseded = pending
        pending = null
        superseded?.dispose()
        return PreviewBundleCandidate(owner, identity, createOwnership(identity)).also {
            pending = it
        }
    }

    fun accepts(generation: Long, sessionId: Long): Boolean {
        if (disposed) return false
        val bundle = current ?: return false
        return bundle.identity.generation == generation &&
            bundle.identity.sessionId == sessionId &&
            bundle.acceptsDeliveries
    }

    fun commit(candidate: PreviewBundleCandidate) {
        ensureCandidateCanCommit(candidate)
        commitInProgress = true
        val previous = current
        try {
            val failure = switchCurrent(previous, candidate)
            if (failure != null) rollback(previous, candidate, failure)
            completeSwitch(previous, candidate)
        } finally {
            commitInProgress = false
        }
    }

    fun dispose() {
        if (disposed) return
        check(!commitInProgress) { "预览 bundle 提交不可重入" }
        commitInProgress = true
        val active = current
        val candidate = pending
        try {
            var failure = detachForDispose(active)
            failure = disposeBundles(candidate, active, failure)
            failure?.let { throw it }
        } finally {
            commitInProgress = false
        }
    }

    private fun ensureCandidateCanCommit(candidate: PreviewBundleCandidate) {
        ensureActive()
        check(!commitInProgress) { "预览 bundle 提交不可重入" }
        check(candidate.belongsTo(owner)) { "候选预览 bundle 不属于当前生命周期" }
        check(pending === candidate) { "只能提交最新候选预览 bundle" }
        candidate.ensureCommittable()
    }

    private fun switchCurrent(
        previous: PreviewBundleCandidate?,
        candidate: PreviewBundleCandidate,
    ): Throwable? {
        commitCriticalSectionActive = true
        return try {
            previous?.freeze()
            candidate.activate()
            candidate.prepareCurrentSwitch()
            current = candidate
            pending = null
            null
        } catch (error: Throwable) {
            pending = null
            error
        } finally {
            commitCriticalSectionActive = false
        }
    }

    /** current 已不可逆切换；post-switch 失败仍先释放旧 bundle 再向上抛出。 */
    private fun completeSwitch(previous: PreviewBundleCandidate?, candidate: PreviewBundleCandidate) {
        var failure = afterSwitchFailure(candidate)
        failure = disposeBundle(previous, failure)
        failure?.let { throw it }
    }

    private fun afterSwitchFailure(candidate: PreviewBundleCandidate): Throwable? = try {
        candidate.completeCurrentSwitch()
        null
    } catch (error: Throwable) {
        error
    }

    private fun rollback(
        previous: PreviewBundleCandidate?,
        candidate: PreviewBundleCandidate,
        failure: Throwable,
    ): Nothing {
        preserveFailure(failure) { candidate.dispose() }
        preserveFailure(failure) { previous?.resume() }
        throw failure
    }

    private fun detachForDispose(active: PreviewBundleCandidate?): Throwable? {
        commitCriticalSectionActive = true
        return try {
            val failure = freezeForDispose(active)
            current = null
            pending = null
            disposed = true
            failure
        } finally {
            commitCriticalSectionActive = false
        }
    }

    private fun freezeForDispose(active: PreviewBundleCandidate?): Throwable? = try {
        active?.freeze()
        null
    } catch (error: Throwable) {
        error
    }

    private fun disposeBundles(
        candidate: PreviewBundleCandidate?,
        active: PreviewBundleCandidate?,
        initialFailure: Throwable?,
    ): Throwable? {
        var failure = initialFailure
        failure = disposeBundle(candidate, failure)
        failure = disposeBundle(active, failure)
        return failure
    }

    private fun disposeBundle(
        bundle: PreviewBundleCandidate?,
        initialFailure: Throwable?,
    ): Throwable? = try {
        bundle?.dispose()
        initialFailure
    } catch (error: Throwable) {
        initialFailure?.also { it.addSuppressed(error) } ?: error
    }

    private fun nextIdentity(): PreviewBundleIdentity {
        check(generationSequence < Long.MAX_VALUE) { "预览 bundle generation 已耗尽" }
        check(sessionSequence < Long.MAX_VALUE) { "预览 bundle sessionId 已耗尽" }
        generationSequence += 1L
        sessionSequence += 1L
        return PreviewBundleIdentity(generationSequence, sessionSequence)
    }

    private fun ensureActive() {
        check(!disposed) { "预览 bundle 生命周期已关闭" }
    }

    private inline fun preserveFailure(failure: Throwable, block: () -> Unit) {
        try {
            block()
        } catch (cleanupError: Throwable) {
            failure.addSuppressed(cleanupError)
        }
    }
}
